"use client";

import { Check, Copy, Smartphone, Video } from "lucide-react";
import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import { useLiveSession } from "@/contexts/live-session-context";
import { performerSources, RECITAL_MAX_SOURCES } from "@/lib/live/recital/recital-video";
import type { VideoPairingLink } from "@/lib/live/video/video-types";
import { QrCode } from "./qr-code";

/**
 * La section « Caméra » du panneau de l'interprète.
 *
 * Deux caméras au plus : la webcam de cet ordinateur, des téléphones appairés
 * par un QR code. Le contexte tient la règle — une troisième qui filme coupe la
 * plus ancienne — ; ce panneau montre les caméras qui filment, de quoi couper
 * chacune, et de quoi en brancher une. Voir `docs/spec/live-camera.md`.
 */
export function CameraControl() {
  const live = useLiveSession();
  const [busy, setBusy] = useState(false);
  const devices = useVideoInputs(live?.localCamera?.deviceId ?? null);

  if (live === null) {
    return null;
  }

  const sources = performerSources(live.videoSources, live.videoSelf);
  const phones = sources.filter((source) => !source.local);
  const recordingOnly = live.session?.recital?.recordingOnly === true;
  const webcam = live.localCamera;
  const curtain = live.curtain;
  /*
    Derrière le rideau, la webcam n'est plus une source de salle : elle en est
    sortie sans s'éteindre, et c'est `localCamera` qui la porte alors. Sans
    cette reprise, le panneau annonçait « aucune caméra » et retirait le bouton
    « Couper » à une caméra qui filme — pendant la minute où l'on est justement
    en train de la régler.

    Un téléphone, lui, disparaît vraiment de la liste le temps du rideau : il
    est dans la salle mais ne publie rien, et rien ici ne le voit. Il revient au
    relevé, et on le coupe alors.
  */
  const localStream = sources.find((source) => source.local)?.stream ?? webcam?.stream ?? null;
  const shown = (localStream === null ? 0 : 1) + phones.length;
  const pairing = live.phonePairing;
  // Tant que la salle n'est pas jointe, il n'y a nulle part où publier : un clic
  // trop tôt échouait avec un message, pour un geste qui aurait abouti une
  // seconde plus tard.
  const ready = live.status === "live";

  const run = async (gesture: () => Promise<unknown>) => {
    setBusy(true);

    try {
      await gesture();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Sans public, la même phrase mentirait deux fois : personne ne regarde,
          et le rouleau est le sien. Ce qu'on y gagne est ailleurs — les mains
          dans l'enregistrement. */}
      {shown === 0 && (
        <p className="text-[12px] leading-relaxed text-text-muted">
          {recordingOnly
            ? "Filme-toi avec une ou deux caméras : elles s'affichent en vignettes sur ton rouleau, et l'enregistrement les garde, sans le son."
            : "Montre-toi au public avec une ou deux caméras : elles s'affichent en vignettes sur le rouleau de chacun, sans le son."}
        </p>
      )}

      {localStream !== null && (
        <Fragment key="webcam">
          <ActiveSource
            stream={localStream}
            mirrored
            live={!curtain}
            label="Webcam"
            detail={curtain ? "hors antenne" : webcam?.label ?? "Webcam"}
            cutLabel="Couper la webcam"
            busy={busy}
            onCut={() => void run(live.stopWebcam)}
          />

          {webcam !== null && devices.length > 1 && (
            <select
              value={webcam.deviceId ?? ""}
              disabled={busy}
              onChange={(event) => void run(() => live.startWebcam(event.target.value))}
              aria-label="Webcam filmée"
              className="h-8 w-full rounded-md border border-border bg-bg-deep px-2 text-[12px] text-text-secondary"
            >
              {devices.map((device, index) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label === "" ? `Caméra ${index + 1}` : device.label}
                </option>
              ))}
            </select>
          )}
        </Fragment>
      )}

      {phones.map((source) => {
        // Deux téléphones portent le même nom : un numéro, dans l'ordre où ils se
        // sont mis à filmer, et leur image à côté pour savoir lequel couper.
        const label = phones.length > 1 ? `Téléphone ${phones.indexOf(source) + 1}` : "Téléphone";

        return (
          <ActiveSource
            key={source.id}
            stream={source.stream}
            mirrored={false}
            live={!source.muted}
            label={label}
            detail={source.muted ? "en pause" : "en direct"}
            cutLabel={`Couper le ${label.toLowerCase()}`}
            busy={busy}
            onCut={() => void run(() => live.removeCamera(source.id))}
          />
        );
      })}

      {curtain && shown > 0 && (
        <p className="text-[12px] leading-relaxed text-amber-300/90">
          Rideau baissé&nbsp;: ces caméras ne sortent pas. Un téléphone se cadre sur son propre écran.
        </p>
      )}

      {shown === RECITAL_MAX_SOURCES && (
        <p className="text-[12px] leading-relaxed text-text-muted">
          Deux caméras sont branchées : en brancher une troisième coupera la plus ancienne.
        </p>
      )}

      {/* Pas pendant qu'elle s'éteint : sa ligne est encore là, et le bouton
          proposerait d'allumer une webcam qui filme encore. */}
      {webcam === null && localStream === null && (
        <SourceButton
          icon={<Video size={14} aria-hidden />}
          disabled={busy || !ready}
          onClick={() => void run(() => live.startWebcam())}
        >
          Webcam de cet ordinateur
        </SourceButton>
      )}

      {pairing === null && (
        <SourceButton
          icon={<Smartphone size={14} aria-hidden />}
          disabled={busy || !ready}
          onClick={() => void run(live.pairPhone)}
        >
          {phones.length === 0 ? "Filmer avec un téléphone" : "Filmer avec un autre téléphone"}
        </SourceButton>
      )}

      {pairing !== null && (
        <PhonePairing pairing={pairing} busy={busy} onCancel={live.cancelPhonePairing} onRenew={() => void run(live.pairPhone)} />
      )}

      {live.cameraError !== null && (
        <p role="alert" className="text-[12px] leading-relaxed text-red-300">
          {live.cameraError}
        </p>
      )}
    </div>
  );
}

function ActiveSource({
  stream,
  mirrored,
  live,
  label,
  detail,
  cutLabel,
  busy,
  onCut,
}: {
  stream: MediaStream;
  mirrored: boolean;
  live: boolean;
  label: string;
  detail: string;
  cutLabel: string;
  busy: boolean;
  onCut: () => void;
}) {
  return (
    <div className="flex h-10 items-center gap-2 rounded-lg bg-bg-elevated pl-1.5 pr-3">
      <SourcePreview stream={stream} mirrored={mirrored} live={live} />
      <span className="shrink-0 text-[12px] font-semibold text-text-primary">{label}</span>
      <span className="min-w-0 flex-1 truncate text-[12px] text-text-muted">{detail}</span>
      <button
        type="button"
        disabled={busy}
        onClick={onCut}
        aria-label={cutLabel}
        className="h-7 shrink-0 cursor-pointer rounded-md bg-red-500/10 px-2.5 text-[12px] font-semibold text-red-300 transition-colors duration-150 hover:bg-red-500/15 disabled:opacity-50"
      >
        Couper
      </button>
    </div>
  );
}

/**
 * Ce que filme la caméra, en tout petit : c'est ce qui distingue deux
 * téléphones, que rien d'autre ne nomme. Le point rouge dit qu'elle filme.
 */
function SourcePreview({ stream, mirrored, live }: { stream: MediaStream; mirrored: boolean; live: boolean }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = videoRef.current;

    if (video === null) {
      return;
    }

    video.srcObject = stream;
  }, [stream]);

  return (
    <div className="relative h-7 w-[50px] shrink-0 overflow-hidden rounded-md bg-black">
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        aria-hidden
        className="h-full w-full object-cover"
        style={{ transform: mirrored ? "scaleX(-1)" : undefined }}
      />

      {live && <span aria-hidden className="absolute left-1 top-1 h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />}
    </div>
  );
}

function SourceButton({
  icon,
  children,
  disabled,
  onClick,
}: {
  icon: ReactNode;
  children: ReactNode;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="inline-flex h-9 w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-bg-elevated text-[12px] font-semibold text-text-secondary transition-colors duration-150 hover:bg-bg-hover hover:text-text-primary disabled:opacity-50"
    >
      {icon}
      {children}
    </button>
  );
}

/**
 * Le QR code du lien, le lien lui-même pour qui l'enverrait par message, et ce
 * qu'il lui reste à vivre. Un lien expiré ne se montre plus : il ne mènerait
 * nulle part.
 */
function PhonePairing({
  pairing,
  busy,
  onCancel,
  onRenew,
}: {
  pairing: VideoPairingLink;
  busy: boolean;
  onCancel: () => void;
  onRenew: () => void;
}) {
  const now = useNow(1000);
  const [copied, setCopied] = useState(false);
  const remainingMs = pairing.expiresAtMs - now;

  if (remainingMs <= 0) {
    return (
      <div className="flex flex-col gap-2 rounded-lg border border-border-subtle bg-bg-deep p-3">
        <p className="text-[12px] leading-relaxed text-text-secondary">
          Le lien a expiré sans qu&apos;aucun téléphone ne l&apos;ouvre.
        </p>
        <div className="flex gap-2">
          <SourceButton icon={<Smartphone size={14} aria-hidden />} disabled={busy} onClick={onRenew}>
            Nouveau lien
          </SourceButton>
          <button
            type="button"
            onClick={onCancel}
            className="h-9 shrink-0 cursor-pointer rounded-lg px-3 text-[12px] text-text-muted hover:text-text-primary"
          >
            Fermer
          </button>
        </div>
      </div>
    );
  }

  const minutes = Math.max(1, Math.ceil(remainingMs / 60_000));

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(pairing.url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Presse-papier refusé : le champ reste sélectionnable.
    }
  };

  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-border-subtle bg-bg-deep p-3">
      <QrCode value={pairing.url} size={176} label="QR code du lien qui fait filmer un téléphone" />

      <p className="text-center text-[12px] leading-relaxed text-text-secondary">
        Scanne ce code avec l&apos;appareil photo du téléphone, puis pose-le face à ce qu&apos;il doit filmer. Le lien
        sert une fois, encore {minutes} min.
      </p>

      <div className="flex w-full items-center gap-1.5">
        <input
          readOnly
          value={pairing.url}
          aria-label="Lien qui fait filmer un téléphone"
          onFocus={(event) => event.currentTarget.select()}
          className="h-8 min-w-0 flex-1 rounded-md border border-border bg-bg-surface px-2 text-[12px] text-text-secondary"
        />
        <button
          type="button"
          onClick={() => void handleCopy()}
          title="Copier le lien"
          aria-label="Copier le lien"
          className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md bg-bg-elevated text-text-secondary transition-colors duration-150 hover:bg-bg-hover hover:text-text-primary"
        >
          {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
        </button>
      </div>

      <button
        type="button"
        onClick={onCancel}
        className="cursor-pointer text-[12px] text-text-muted transition-colors hover:text-text-primary"
      >
        Annuler
      </button>
    </div>
  );
}

/** Les webcams de ce poste, relues quand la caméra filmée change ou qu'un périphérique arrive. */
function useVideoInputs(activeDeviceId: string | null): readonly MediaDeviceInfo[] {
  const [devices, setDevices] = useState<readonly MediaDeviceInfo[]>([]);

  useEffect(() => {
    // Avant la première permission, le navigateur ne rend ni noms ni
    // identifiants : la liste ne servirait à rien.
    if (activeDeviceId === null || typeof navigator === "undefined" || navigator.mediaDevices === undefined) {
      return;
    }

    let cancelled = false;

    const refresh = async () => {
      const all = await navigator.mediaDevices.enumerateDevices().catch(() => [] as MediaDeviceInfo[]);

      if (!cancelled) {
        setDevices(all.filter((device) => device.kind === "videoinput" && device.deviceId !== ""));
      }
    };

    void refresh();
    navigator.mediaDevices.addEventListener("devicechange", refresh);

    return () => {
      cancelled = true;
      navigator.mediaDevices.removeEventListener("devicechange", refresh);
    };
  }, [activeDeviceId]);

  return devices;
}

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);

    return () => {
      window.clearInterval(timer);
    };
  }, [intervalMs]);

  return now;
}
